import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, MapIcon, Eye, MessageCircle, Search, Keyboard, Star, ShieldCheck, ShieldAlert, RefreshCw, Download, Trash2, Upload, Video } from 'lucide-react';
import { openCheckout, isNativeCheckoutSupported } from '../../utils/openCheckout';
import { formatEuropeanDateTime } from '../../utils/dateFormat';
import { buildVenueMapItem } from './AdminGeoMapExplorer';
import { AdminGeoMapCanvas } from './AdminGeoMapCanvas';
import { AdminDetailSection, Badge, Card, Field, TimelinePreview, adminDetailFieldCardStyle } from './AdminPanelPrimitives';
import { StatusChip, STATUS_PALETTE } from './adminDesignSystem';

// ───────────────────────────────────────────────────────────────
// AdminVenueMediaCard — in-admin management of venue photos + the
// intro video. Admins can download, delete and upload replacement
// media directly from a venue's detail view. Replaces the previous
// read-only "Venue Photos" grid, and adds the previously-missing
// intro video section (admins reported they couldn't see or fix
// bad intro videos uploaded by owners).
// ───────────────────────────────────────────────────────────────
const AdminVenueMediaCard = ({ backendUrl, selectedVenueApp, setSelectedVenueApp }) => {
  const [photoBusyUrl, setPhotoBusyUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const appId = selectedVenueApp.id;
  const photoUrls = selectedVenueApp.photo_urls || [];
  const introVideoUrl = selectedVenueApp.intro_video_url || '';
  const absolute = (url) => (url?.startsWith('/') ? `${backendUrl}${url}` : url);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Persist a photo_urls or intro_video_url mutation via the admin
  // edit endpoint, then reflect the change locally so the detail
  // view updates instantly without waiting for a full list refetch.
  const persistEdit = async (patch) => {
    await axios.put(`${backendUrl}/api/admin/venue-applications/${appId}/edit`, patch, {
      headers: authHeaders(),
    });
    setSelectedVenueApp((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  // Trigger a browser download for an admin-served file.
  //
  // On NATIVE Capacitor (Android/iOS WebView) `fetch()` + blob + anchor
  // download does NOT trigger a system download — the WebView ignores
  // the `download` attribute AND `window.open(url, '_blank')` is a
  // no-op. The user reported "save button not working" on native
  // Android for exactly this reason. We route through `openCheckout`
  // which opens the asset URL in Chrome Custom Tabs (Android) or
  // SFSafariViewController (iOS); from there the native browser's
  // own menu ("Download image", "Save video") completes the save.
  //
  // On WEB we keep the blob-fetch + anchor-click path so the download
  // happens silently without opening a new tab. On CORS failure we
  // still fall back to `window.open` so the admin can save via the
  // browser's native context menu.
  const triggerDownload = async (url, filename) => {
    const absoluteUrl = absolute(url);
    if (isNativeCheckoutSupported()) {
      try {
        await openCheckout(absoluteUrl);
      } catch (_err) {
        toast.error('Could not open the file. Please try again.');
      }
      return;
    }
    try {
      const res = await fetch(absoluteUrl);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || url.split('/').pop() || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      window.open(absoluteUrl, '_blank', 'noopener');
    }
  };

  const handleDeletePhoto = async (url) => {
    if (!window.confirm('Delete this photo from the venue? Guests will stop seeing it.')) return;
    setPhotoBusyUrl(url);
    try {
      const next = photoUrls.filter((entry) => entry !== url);
      await persistEdit({ photo_urls: next, main_photo: next[0] || '' });
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete photo');
    } finally {
      setPhotoBusyUrl('');
    }
  };

  const handleUploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large (max 10MB)');
      return;
    }
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${backendUrl}/api/venue/upload-photo`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const next = [...photoUrls, res.data.url];
      await persistEdit({ photo_urls: next, main_photo: next[0] || '' });
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteVideo = async () => {
    if (!window.confirm('Delete the intro video? The venue card will go back to showing just photos.')) return;
    setVideoBusy(true);
    try {
      await persistEdit({ intro_video_url: '' });
      toast.success('Intro video removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete video');
    } finally {
      setVideoBusy(false);
    }
  };

  const handleUploadVideo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video too large (max 50MB)');
      return;
    }
    setUploadingVideo(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${backendUrl}/api/venue/upload-video`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      await persistEdit({ intro_video_url: res.data.url });
      toast.success('Intro video uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingVideo(false);
    }
  };

  const actionBtnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    padding: '6px 10px', borderRadius: 8, border: 'none',
    fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
    background: 'rgba(15,17,21,0.78)', color: '#FFFFFF',
    backdropFilter: 'blur(4px)',
  };

  return (
    <>
      <Card data-testid="admin-venue-photos-card" style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Venue Photos</p>
          <button
            type="button"
            data-testid="admin-venue-photo-upload-btn"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhoto}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 9999,
              border: '1px solid #E5E7EB', background: '#FFFFFF',
              color: '#3B404B', cursor: uploadingPhoto ? 'wait' : 'pointer',
              fontWeight: 700, fontSize: 12.5,
            }}
          >
            <Upload size={13} /> {uploadingPhoto ? 'Uploading…' : 'Upload photo'}
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            data-testid="admin-venue-photo-upload-input"
            style={{ display: 'none' }}
            onChange={handleUploadPhoto}
          />
        </div>
        {photoUrls.length === 0 ? (
          <p style={{ fontSize: 13, color: '#999', margin: 0 }}>No photos uploaded</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {photoUrls.map((url, index) => (
              <div
                key={`${appId}-${url}`}
                data-testid={`admin-venue-photo-tile-${index}`}
                style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(112, 63, 246, 0.1)', boxShadow: 'var(--premium-shadow-nested)' }}
              >
                <img
                  src={absolute(url)}
                  alt={`Photo ${index + 1}`}
                  style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                />
                <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(15,17,21,0.72)', color: '#FFFFFF', borderRadius: 9999, padding: '2px 8px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.03em' }}>
                  {index === 0 ? 'MAIN' : `#${index + 1}`}
                </div>
                <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6, display: 'flex', gap: 6, justifyContent: 'space-between' }}>
                  <button
                    type="button"
                    data-testid={`admin-venue-photo-download-${index}`}
                    title="Download photo"
                    onClick={() => triggerDownload(url, `venue-${appId}-photo-${index + 1}.jpg`)}
                    style={actionBtnStyle}
                  >
                    <Download size={12} /> Save
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-venue-photo-delete-${index}`}
                    title="Delete photo"
                    onClick={() => handleDeletePhoto(url)}
                    disabled={photoBusyUrl === url}
                    style={{ ...actionBtnStyle, background: 'rgba(221,6,8,0.85)' }}
                  >
                    <Trash2 size={12} /> {photoBusyUrl === url ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card data-testid="admin-venue-intro-video-card" style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 15, fontWeight: 700, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Video size={15} /> Intro Video
          </p>
          <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="admin-venue-intro-video-upload-btn"
              onClick={() => videoInputRef.current?.click()}
              disabled={uploadingVideo}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 9999,
                border: '1px solid #E5E7EB', background: '#FFFFFF',
                color: '#3B404B', cursor: uploadingVideo ? 'wait' : 'pointer',
                fontWeight: 700, fontSize: 12.5,
              }}
            >
              <Upload size={13} /> {uploadingVideo ? 'Uploading…' : introVideoUrl ? 'Replace' : 'Upload video'}
            </button>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              data-testid="admin-venue-intro-video-upload-input"
              style={{ display: 'none' }}
              onChange={handleUploadVideo}
            />
          </div>
        </div>

        {introVideoUrl ? (
          <>
            <video
              key={introVideoUrl}
              src={absolute(introVideoUrl)}
              controls
              playsInline
              preload="metadata"
              data-testid="admin-venue-intro-video-player"
              style={{ width: '100%', maxHeight: 420, borderRadius: 14, background: '#000', display: 'block' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                data-testid="admin-venue-intro-video-download-btn"
                onClick={() => triggerDownload(introVideoUrl, `venue-${appId}-intro.mp4`)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10, border: '1px solid #E5E7EB',
                  background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                }}
              >
                <Download size={14} /> Download
              </button>
              <button
                type="button"
                data-testid="admin-venue-intro-video-delete-btn"
                onClick={handleDeleteVideo}
                disabled={videoBusy}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10, border: '1px solid #F3C9CA',
                  background: '#FFF5F5', color: '#DD0608', cursor: videoBusy ? 'wait' : 'pointer', fontWeight: 700, fontSize: 13,
                }}
              >
                <Trash2 size={14} /> {videoBusy ? 'Removing…' : 'Delete video'}
              </button>
            </div>
          </>
        ) : (
          <p style={{ fontSize: 13, color: '#999', margin: 0 }}>No intro video uploaded.</p>
        )}
      </Card>
    </>
  );
};

export const AdminVenueManagementPanel = ({
  backendUrl,
  editData,
  editMode,
  expandedTimelineId,
  movePhoto,
  mobileMode = false,
  onContactUser,
  onSaveVenueEdit,
  onViewReviews,
  searchQuery,
  selectedVenueApp,
  setEditData,
  setEditMode,
  setSearchQuery,
  setSelectedVenueApp,
  setExpandedTimelineId,
  startAdminConversation,
  onViewOnMap,
  venueAction,
  venueApps,
  venues,
}) => {
  const [selectedMapId, setSelectedMapId] = useState('');
  const [venueStatusFilter, setVenueStatusFilter] = useState('all');
  const [aadeRechecking, setAadeRechecking] = useState(false);
  const normalizeVenueStatus = (status) => status;

  // AADE verification classifier helpers. `aade_verification` is stamped
  // onto the venue application at submission time (see backend
  // routes/venue_applications.py). Legacy applications submitted before
  // this rollout won't carry the field — we treat those as
  // `not_attempted` for filtering purposes.
  const AADE_STATUS_LABEL = {
    verified: 'AADE verified',
    name_mismatch: 'AADE name mismatch',
    no_activity: 'No registered business activity',
    not_found: 'ΑΦΜ not found in AADE',
    rate_limited: 'AADE rate-limited at submit',
    unreachable: 'AADE unreachable at submit',
    not_attempted: 'ΑΦΜ verification not attempted',
  };
  const AADE_STATUS_TONE = {
    verified: '#00A855',
    name_mismatch: '#B56B11',
    no_activity: '#DD0608',
    not_found: '#DD0608',
    rate_limited: '#6B7280',
    unreachable: '#6B7280',
    not_attempted: '#9CA3AF',
  };
  const aadeStatusOf = (app) => app?.aade_verification?.status || (app?.business_vat ? 'not_attempted' : '');
  const needsAadeReview = (app) => {
    const s = aadeStatusOf(app);
    return !!s && s !== 'verified';
  };

  // Map venue_application.id → live venue document so the "Reviews" CTA
  // can resolve the public venue.id when the admin is viewing the
  // application card. Only live venues have a backing venue doc.
  const venueByAppId = useMemo(() => {
    const map = new Map();
    (venues || []).forEach((v) => {
      if (v?.application_id) map.set(v.application_id, v);
    });
    return map;
  }, [venues]);

  // Index live venues by application_id so we can surface the "hidden from
  // guests — no payout connected" state. The /admin/venues endpoint annotates
  // each venue with `is_on_hold` based on connected payout methods.
  const payoutHoldByAppId = useMemo(() => {
    const map = new Map();
    (venues || []).forEach((v) => {
      if (v?.application_id) map.set(v.application_id, !!v.is_on_hold);
    });
    return map;
  }, [venues]);

  // Same map but for the latest audit-log entry per venue, used to surface
  // "Last payout change · 4d ago" badges on the admin card.
  const lastPayoutChangeByAppId = useMemo(() => {
    const map = new Map();
    (venues || []).forEach((v) => {
      if (v?.application_id && v?.last_payout_change) {
        map.set(v.application_id, v.last_payout_change);
      }
    });
    return map;
  }, [venues]);

  const formatRelativeShort = (iso) => {
    if (!iso) return '';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const days = Math.floor(diff / 86400000);
    if (days >= 1) return `${days}d ago`;
    const hours = Math.floor(diff / 3600000);
    if (hours >= 1) return `${hours}h ago`;
    const mins = Math.floor(diff / 60000);
    return `${mins}m ago`;
  };
  const formatEvent = (entry) => {
    const m = (x) => x === 'viva_wallet' ? 'Viva' : x === 'iban' ? 'IBAN' : '';
    if (entry.event_type === 'connected') return `Connected ${m(entry.to_method)}`;
    if (entry.event_type === 'disconnected') return `Disconnected ${m(entry.from_method)}`;
    if (entry.event_type === 'switched') return `Switched ${m(entry.from_method)} → ${m(entry.to_method)}`;
    if (entry.event_type === 'details_updated') return `Updated ${m(entry.to_method)} details`;
    return entry.event_type;
  };
  const searchedVenueApps = venueApps.filter((app) => app.status !== 'rejected').filter((app) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return [app.user?.email, app.user?.customer_id, app.restaurant_name].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
  });
  const visibleVenueApps = searchedVenueApps.filter((app) => {
    const s = app.status;
    if (venueStatusFilter === 'all') return true;
    if (venueStatusFilter === 'active') return s === 'live';
    if (venueStatusFilter === 'pending_approval') return s === 'submitted';
    if (venueStatusFilter === 'in_progress') return s === 'setup_mode' || s === 'draft' || s === 'revisions_requested';
    if (venueStatusFilter === 'awaiting_payout') return s === 'approved';
    if (venueStatusFilter === 'on_hold') return s === 'on_hold';
    if (venueStatusFilter === 'aade_review') return needsAadeReview(app);
    return false;
  });
  const mapItems = useMemo(() => visibleVenueApps.map(buildVenueMapItem).filter(Boolean), [visibleVenueApps]);
  const selectedMapItemId = selectedMapId || mapItems[0]?.id || '';

  // ── Keyboard shortcuts ────────────────────────────────────────────────
  // Power-admin nav: ↑/↓ moves focus between venue cards, O / Enter opens
  // details, M views on map, C contacts the user. Ignored when typing in
  // an input or when a detail view is open.
  const cardRefs = useRef({});
  const focusedAppRef = useRef(null);
  useEffect(() => {
    if (!visibleVenueApps.length) return undefined;
    const focusedId = (selectedMapItemId || '').replace(/^venue-/, '');
    focusedAppRef.current = visibleVenueApps.find((entry) => entry.id === focusedId) || visibleVenueApps[0];

    const handleKey = (event) => {
      const tag = (event.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || event.target?.isContentEditable) return;
      if (selectedVenueApp) return;
      const idx = Math.max(0, visibleVenueApps.findIndex((entry) => entry.id === focusedAppRef.current?.id));
      if (event.key === 'ArrowDown' || event.key === 'j') {
        event.preventDefault();
        const next = visibleVenueApps[Math.min(visibleVenueApps.length - 1, idx + 1)];
        if (next) {
          setSelectedMapId(`venue-${next.id}`);
          cardRefs.current[next.id]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (event.key === 'ArrowUp' || event.key === 'k') {
        event.preventDefault();
        const prev = visibleVenueApps[Math.max(0, idx - 1)];
        if (prev) {
          setSelectedMapId(`venue-${prev.id}`);
          cardRefs.current[prev.id]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (event.key === 'o' || event.key === 'O' || event.key === 'Enter') {
        const target = focusedAppRef.current;
        if (target) {
          event.preventDefault();
          setSelectedVenueApp({ ...target, status: normalizeVenueStatus(target.status) });
          setEditMode(false);
          setEditData({});
        }
      } else if (event.key === 'm' || event.key === 'M') {
        const target = focusedAppRef.current;
        if (target) {
          event.preventDefault();
          onViewOnMap(`venue-${target.id}`, [normalizeVenueStatus(target.status) === 'live' ? 'active-venues' : 'pending-venues']);
        }
      } else if (event.key === 'c' || event.key === 'C') {
        const target = focusedAppRef.current;
        if (target) {
          event.preventDefault();
          onContactUser(target.user?.id || target.user_id);
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [visibleVenueApps, selectedVenueApp, selectedMapItemId, onViewOnMap, onContactUser, setSelectedVenueApp, setEditMode, setEditData]);

  if (!selectedVenueApp) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: mobileMode ? '1fr' : 'minmax(0, 1.1fr) minmax(360px, 0.9fr)', gap: 14, alignItems: 'start' }}>
        {/* Mobile: map shown ABOVE the list */}
        {mobileMode && (
          <AdminGeoMapCanvas
            dataTestId="admin-venues-inline-map-mobile"
            emptyCopy="Venues with coordinates will appear here."
            items={mapItems}
            onSelect={setSelectedMapId}
            selectedId={selectedMapItemId}
            title="Venues map"
            height={280}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Card style={{ marginBottom: 4 }}>
            <div style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E7E7E7', borderRadius: 14, padding: '12px 14px' }}>
              <Search size={18} color="#666" />
              <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} data-testid="admin-venues-search-input" placeholder="Search venues by e-mail or Account ID" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 15 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <StatusChip tone="neutral" label="All" count={searchedVenueApps.length} active={venueStatusFilter === 'all'} onClick={() => setVenueStatusFilter('all')} dataTestId="admin-venues-filter-all" />
              <StatusChip tone="venue" label="Active" count={searchedVenueApps.filter((app) => app.status === 'live').length} active={venueStatusFilter === 'active'} onClick={() => setVenueStatusFilter('active')} dataTestId="admin-venues-filter-active" />
              <StatusChip tone="pending" label="Pending approval" count={searchedVenueApps.filter((app) => app.status === 'submitted').length} active={venueStatusFilter === 'pending_approval'} onClick={() => setVenueStatusFilter('pending_approval')} dataTestId="admin-venues-filter-pending-approval" />
              <StatusChip tone="info" label="In Progress" count={searchedVenueApps.filter((app) => app.status === 'setup_mode' || app.status === 'draft' || app.status === 'revisions_requested').length} active={venueStatusFilter === 'in_progress'} onClick={() => setVenueStatusFilter('in_progress')} dataTestId="admin-venues-filter-in-progress" />
              <StatusChip tone="early" label="Awaiting payout" count={searchedVenueApps.filter((app) => app.status === 'approved').length} active={venueStatusFilter === 'awaiting_payout'} onClick={() => setVenueStatusFilter('awaiting_payout')} dataTestId="admin-venues-filter-awaiting-payout" />
              <StatusChip tone="hold" label="On hold" count={searchedVenueApps.filter((app) => app.status === 'on_hold').length} active={venueStatusFilter === 'on_hold'} onClick={() => setVenueStatusFilter('on_hold')} dataTestId="admin-venues-filter-on-hold" />
              <StatusChip tone="urgent" label="AADE review" count={searchedVenueApps.filter(needsAadeReview).length} active={venueStatusFilter === 'aade_review'} onClick={() => setVenueStatusFilter('aade_review')} dataTestId="admin-venues-filter-aade-review" />
              <span
                data-testid="admin-venues-keyboard-hints"
                title="Keyboard shortcuts on venue cards"
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7280', background: '#F6F7F9', border: '1px solid #E5E7EB', borderRadius: 9999, padding: '5px 10px', fontWeight: 600 }}
              >
                <Keyboard size={12} /> ↑↓ navigate · O open · M map · C contact
              </span>
            </div>
          </Card>

          {visibleVenueApps.length === 0 ? <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>No venue cards found</p> : null}
          {visibleVenueApps.map((app) => {
            const status = app.status;
            const normalized = normalizeVenueStatus(status);
            const isAwaitingPayout = status === 'approved' || (status === 'live' && payoutHoldByAppId.get(app.id) === true);
            const isAdminHeld = status === 'on_hold' && !!app.admin_hold_reason;
            const isLegacyOnHold = status === 'on_hold' && !app.admin_hold_reason;
            const stalledAt = app.stalled_at;
            const heldDays = app.admin_held_at ? Math.max(0, Math.floor((Date.now() - new Date(app.admin_held_at).getTime()) / 86400000)) : null;

            const primaryBadge =
              status === 'live' && !isAwaitingPayout ? { text: 'LIVE', tone: 'venue' } :
              isAdminHeld ? { text: 'ADMIN HOLD', tone: 'hold' } :
              isAwaitingPayout ? { text: 'AWAITING PAYOUT', tone: 'early' } :
              status === 'submitted' ? { text: 'PENDING APPROVAL', tone: 'pending' } :
              status === 'setup_mode' ? { text: 'IN SETUP', tone: 'pending' } :
              status === 'draft' ? { text: 'DRAFT', tone: 'pending' } :
              status === 'revisions_requested' ? { text: 'REVISIONS REQUESTED', tone: 'pending' } :
              status === 'rejected' ? { text: 'REJECTED', tone: 'urgent' } :
              { text: (normalized || 'pending').toUpperCase(), tone: 'pending' };
            const cardPalette = STATUS_PALETTE[primaryBadge.tone] || STATUS_PALETTE.neutral;
            const isSelected = selectedMapItemId === `venue-${app.id}`;

            return (
            <Card
              key={app.id}
              data-testid={`admin-venue-card-${app.id}`}
              ref={(el) => { if (el) cardRefs.current[app.id] = el; }}
              style={{
                cursor: 'pointer',
                padding: 0,
                overflow: 'hidden',
                border: isSelected ? `1px solid ${cardPalette.solid}66` : '1px solid #E7E7E7',
                boxShadow: isSelected ? `0 10px 24px ${cardPalette.solid}1f` : '0 4px 12px rgba(17,17,17,0.04)',
                position: 'relative',
              }}
            >
              <div onClick={() => setSelectedMapId(`venue-${app.id}`)} style={{ display: 'flex', alignItems: 'stretch' }}>
                <div aria-hidden="true" style={{ width: 5, background: cardPalette.solid, flexShrink: 0 }} />
                <div style={{ flex: 1, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#0F1115' }}>{app.restaurant_name || 'Unnamed Venue'}</p>
                      <p style={{ fontSize: 12, color: '#6B7280', margin: '3px 0 0' }}>
                        {app.first_name} {app.last_name} · {app.contact_email}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 11.5, color: '#9CA3AF' }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#374151' }}>{app.user?.customer_id || '—'}</span>
                        {' · '}{app.city || '—'}{app.country ? `, ${app.country}` : ''}
                        {' · Step '}{app.current_step}/7
                      </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                      <Badge text={primaryBadge.text} tone={primaryBadge.tone} />
                      {stalledAt && isAwaitingPayout ? <Badge text="STALLED · 7d+" tone="urgent" /> : null}
                      {isAdminHeld && heldDays !== null && heldDays >= 5 ? <Badge text={`HELD · ${heldDays}d`} tone="urgent" /> : null}
                      {needsAadeReview(app) ? (
                        <span
                          data-testid={`admin-venue-aade-flag-${app.id}`}
                          title={AADE_STATUS_LABEL[aadeStatusOf(app)] || 'AADE review'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', borderRadius: 999,
                            background: `${AADE_STATUS_TONE[aadeStatusOf(app)] || '#B56B11'}1A`,
                            color: AADE_STATUS_TONE[aadeStatusOf(app)] || '#B56B11',
                            border: `1px solid ${AADE_STATUS_TONE[aadeStatusOf(app)] || '#B56B11'}55`,
                            fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                          }}
                        >
                          <ShieldAlert size={10} strokeWidth={2.4} />
                          AADE
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {isAwaitingPayout ? (
                    <p data-testid={`admin-venue-hidden-reason-${app.id}`} style={{ margin: '8px 0 0', fontSize: 12, color: cardPalette.text, fontWeight: 600, background: cardPalette.soft, padding: '6px 10px', borderRadius: 8 }}>
                      Hidden from guests — owner hasn’t connected a payout method yet. Will auto-go-live the moment they do.
                    </p>
                  ) : isAdminHeld ? (
                    <p data-testid={`admin-venue-hold-reason-${app.id}`} style={{ margin: '8px 0 0', fontSize: 12, color: cardPalette.text, fontWeight: 600, background: cardPalette.soft, padding: '6px 10px', borderRadius: 8 }}>
                      On hold by Allure{heldDays !== null ? ` · ${heldDays}d` : ''} · “{app.admin_hold_reason}”
                    </p>
                  ) : isLegacyOnHold ? (
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: cardPalette.text, fontWeight: 600, background: cardPalette.soft, padding: '6px 10px', borderRadius: 8 }}>
                      Legacy on-hold state — open the venue to migrate to the new approval flow.
                    </p>
                  ) : null}

                  {(() => {
                    const change = lastPayoutChangeByAppId.get(app.id);
                    if (!change) return null;
                    return (
                      <p data-testid={`admin-venue-last-payout-change-${app.id}`} style={{ margin: '8px 0 0', fontSize: 11.5, color: '#6B7280', fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>
                        {formatEvent(change)} · {formatRelativeShort(change.created_at)} · {change.actor_role === 'admin' ? 'by Allure' : 'by owner'}
                      </p>
                    );
                  })()}

                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Badge text={app.venue_type || 'restaurant'} tone="info" />
                    {app.photo_urls?.length > 0 ? <Badge text={`${app.photo_urls.length} photos`} tone="active" /> : null}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); setSelectedVenueApp({ ...app, status: normalizeVenueStatus(app.status) }); setEditMode(false); setEditData({}); }}
                      data-testid={`admin-venue-open-details-${app.id}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none', background: '#0F1115', color: '#FFFFFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
                    >
                      <Eye size={14} /> Open details
                    </button>
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); onViewOnMap(`venue-${app.id}`, [normalizeVenueStatus(app.status) === 'live' ? 'active-venues' : 'pending-venues']); }}
                      data-testid={`admin-venue-view-on-map-${app.id}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                    >
                      <MapIcon size={14} /> Map
                    </button>
                    {app.status === 'live' && venueByAppId.get(app.id) ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          const venue = venueByAppId.get(app.id);
                          onViewReviews?.({ venueId: venue.id, venueName: venue.name || app.restaurant_name });
                        }}
                        data-testid={`admin-venue-view-reviews-${app.id}`}
                        title="Inspect guest reviews + reply"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                      >
                        <Star size={14} /> Reviews
                        {typeof venueByAppId.get(app.id)?.review_count === 'number' && venueByAppId.get(app.id).review_count > 0 ? (
                          <span style={{ marginLeft: 2, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#6B7280' }}>
                            {venueByAppId.get(app.id).review_count}
                          </span>
                        ) : null}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); onContactUser(app.user?.id || app.user_id); }}
                      data-testid={`admin-venue-contact-user-${app.id}`}
                      style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                    >
                      <MessageCircle size={14} /> Contact
                    </button>
                  </div>

                  <TimelinePreview
                    timelineId={`venue-${app.id}`}
                    expandedTimelineId={expandedTimelineId}
                    setExpandedTimelineId={setExpandedTimelineId}
                    items={[
                      { label: 'Latest admin-visible update', value: formatEuropeanDateTime(app.updated_at || app.created_at) },
                      { label: 'Venue status', value: app.status },
                      { label: 'Account created', value: formatEuropeanDateTime(app.user?.created_at) },
                    ]}
                  />
                </div>
              </div>
            </Card>
            );
          })}
        </div>

        {/* Desktop: map in right column; Mobile: hidden (shown above instead) */}
        {!mobileMode && (
          <AdminGeoMapCanvas
            dataTestId="admin-venues-inline-map"
            emptyCopy="Venues with coordinates will appear here."
            items={mapItems}
            onSelect={setSelectedMapId}
            selectedId={selectedMapItemId}
            title="Venues map"
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => setSelectedVenueApp(null)} data-testid="admin-venue-detail-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--accent-purple-strong)', marginBottom: 12, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        ← Back to list
      </button>

      <Card style={{ marginBottom: 12 }} data-testid="admin-venue-detail-header-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{selectedVenueApp.restaurant_name}</p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            {selectedVenueApp.status === 'live' && venueByAppId.get(selectedVenueApp.id) ? (
              <button
                type="button"
                onClick={() => {
                  const venue = venueByAppId.get(selectedVenueApp.id);
                  onViewReviews?.({ venueId: venue.id, venueName: venue.name || selectedVenueApp.restaurant_name });
                }}
                data-testid="admin-venue-detail-view-reviews"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 9999, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 700, fontSize: 12.5 }}
              >
                <Star size={13} /> Reviews
                {typeof venueByAppId.get(selectedVenueApp.id)?.review_count === 'number' && venueByAppId.get(selectedVenueApp.id).review_count > 0 ? (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#6B7280' }}>
                    {venueByAppId.get(selectedVenueApp.id).review_count}
                  </span>
                ) : null}
              </button>
            ) : null}
            {(() => {
              const status = selectedVenueApp.status;
              const isOnHoldPayout = status === 'approved' || (status === 'live' && payoutHoldByAppId.get(selectedVenueApp.id) === true);
              const label = status === 'live' ? 'LIVE' : status === 'approved' ? 'AWAITING PAYOUT' : status === 'on_hold' ? 'ADMIN HOLD' : status === 'rejected' ? 'REJECTED' : (status || '').toUpperCase();
              const color = status === 'live' && !isOnHoldPayout ? '#00C853' : status === 'on_hold' ? '#DD0608' : '#B56B11';
              return <span style={{ fontSize: 12, fontWeight: 700, color, textTransform: 'uppercase' }}>{label}</span>;
            })()}
          </div>
        </div>

        {/* Payout method status box */}
        <div data-testid="admin-venue-payout-status" style={{
          marginBottom: 12,
          padding: '10px 12px',
          borderRadius: 10,
          background: payoutHoldByAppId.get(selectedVenueApp.id) === false ? 'rgba(0, 200, 83, 0.06)' : 'rgba(214, 137, 16, 0.06)',
          border: payoutHoldByAppId.get(selectedVenueApp.id) === false ? '1px solid rgba(0, 200, 83, 0.25)' : '1px solid rgba(214, 137, 16, 0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ width: 28, height: 28, borderRadius: 9, background: payoutHoldByAppId.get(selectedVenueApp.id) === false ? '#00C853' : '#B56B11', color: '#FFF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>
            {payoutHoldByAppId.get(selectedVenueApp.id) === false ? '✓' : '!'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111' }}>
              {payoutHoldByAppId.get(selectedVenueApp.id) === false ? 'Payout method connected' : 'No payout method connected'}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#666' }}>
              {payoutHoldByAppId.get(selectedVenueApp.id) === false
                ? 'Owner can receive money — venue will auto-go-live the moment you approve.'
                : 'Owner must connect Viva Wallet or IBAN before this venue can appear on the map.'}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {selectedVenueApp.status === 'on_hold' ? (
            <button onClick={() => venueAction(selectedVenueApp.id, 'unhold')} data-testid="venue-unhold-btn" style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #00C853', cursor: 'pointer', background: '#FFF', color: '#00C853', fontWeight: 700, fontSize: 14 }}>Lift hold</button>
          ) : (
            <button
              onClick={() => {
                const reason = window.prompt('Why are you placing this venue on hold? (the venue will be hidden from guests, you’ll be reminded in 5 days)');
                const trimmed = (reason || '').trim();
                if (!trimmed) return;
                venueAction(selectedVenueApp.id, 'hold', { reason: trimmed });
              }}
              data-testid="venue-hold-btn"
              style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #DDD', cursor: 'pointer', background: '#FFF', color: '#2F79EA', fontWeight: 700, fontSize: 14 }}
            >
              Place on hold
            </button>
          )}
          <button onClick={() => { if (window.confirm('Reject and delete this venue?')) venueAction(selectedVenueApp.id, 'reject'); }} data-testid="venue-reject-btn" style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: selectedVenueApp.status === 'rejected' ? '2px solid #DD0608' : '1px solid #DDD', cursor: 'pointer', background: selectedVenueApp.status === 'rejected' ? '#FFE9E8' : '#FFF', color: '#DD0608', fontWeight: 700, fontSize: 14 }}>Reject</button>
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }} data-testid="admin-venue-map-listing-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Map Listing</p>
          {!editMode ? (
            <button onClick={() => { setEditMode(true); setEditData({ map_name: selectedVenueApp.map_name || selectedVenueApp.restaurant_name, map_description: selectedVenueApp.map_description || selectedVenueApp.venue_type || '', main_photo: selectedVenueApp.photo_urls?.[0] || selectedVenueApp.main_photo || '', category: selectedVenueApp.venue_type || '', photo_urls: [...(selectedVenueApp.photo_urls || [])] }); }} style={{ fontSize: 13, color: '#2F79EA', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => onSaveVenueEdit(selectedVenueApp.id)} style={{ fontSize: 13, color: '#00C853', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Save</button>
              <button onClick={() => setEditMode(false)} style={{ fontSize: 13, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
            </div>
          )}
        </div>
        {editMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontSize: 12, color: '#666' }}>Name on Map
              <input value={editData.map_name || ''} onChange={(event) => setEditData((previous) => ({ ...previous, map_name: event.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(112, 63, 246, 0.1)', boxShadow: 'var(--premium-shadow-nested)', fontSize: 14, marginTop: 2, boxSizing: 'border-box' }} />
            </label>
            <label style={{ fontSize: 12, color: '#666' }}>Description on Map
              <textarea value={editData.map_description || ''} onChange={(event) => setEditData((previous) => ({ ...previous, map_description: event.target.value.slice(0, 60) }))} maxLength={60} rows={3} style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(112, 63, 246, 0.1)', boxShadow: 'var(--premium-shadow-nested)', fontSize: 14, marginTop: 2, resize: 'vertical', boxSizing: 'border-box' }} />
              <span style={{ fontSize: 11, color: '#999', display: 'block', marginTop: 2 }}>Shown as the subtitle on the guest map card ({(editData.map_description || '').length}/60). Owners can also edit this in Venue Card settings.</span>
            </label>
            <label style={{ fontSize: 12, color: '#666' }}>Category
              <input value={editData.category || ''} onChange={(event) => setEditData((previous) => ({ ...previous, category: event.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(112, 63, 246, 0.1)', boxShadow: 'var(--premium-shadow-nested)', fontSize: 14, marginTop: 2, boxSizing: 'border-box' }} />
            </label>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#666', margin: 0 }}>Main Photo</p>
              <p style={{ fontSize: 12, color: '#999', margin: '4px 0 8px' }}>The first photo in the order below is always used as the main photo.</p>
              {(editData.photo_urls || []).length > 0 ? (
                <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                  {editData.photo_urls.map((url, index) => (
                    <div key={url} style={{ minWidth: 132 }}>
                      <img src={url.startsWith('/') ? `${backendUrl}${url}` : url} alt={`Preview ${index + 1}`} data-testid={`venue-photo-preview-${index}`} style={{ width: 132, height: 132, objectFit: 'cover', borderRadius: 12, border: index === 0 ? '3px solid #2F79EA' : '2px solid #EEE' }} />
                      <p style={{ fontSize: 12, fontWeight: 600, color: index === 0 ? '#2F79EA' : '#666', margin: '6px 0 0' }}>Photo {index + 1}{index === 0 ? ' (Main)' : ''}</p>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 12, color: '#999', margin: '4px 0 0' }}>No photos uploaded</p>}
            </div>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#666', margin: '8px 0 0' }}>Photo Order (reorder for map gallery)</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(editData.photo_urls || []).map((url, index) => (
                <div key={`${selectedVenueApp.id}-${url}`} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F8F8F8', borderRadius: 8, padding: 6 }}>
                  <img src={url.startsWith('/') ? `${backendUrl}${url}` : url} alt={`Photo ${index + 1}`} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, color: '#555' }}>Photo {index + 1}{index === 0 ? ' (Main)' : ''}</span>
                  <button onClick={() => movePhoto(index, index - 1)} disabled={index === 0} data-testid={`photo-order-up-${index}`} style={{ background: 'none', border: 'none', cursor: index > 0 ? 'pointer' : 'default', opacity: index > 0 ? 1 : 0.3, padding: 2 }}><ChevronUp size={18} /></button>
                  <button onClick={() => movePhoto(index, index + 1)} disabled={index === (editData.photo_urls || []).length - 1} data-testid={`photo-order-down-${index}`} style={{ background: 'none', border: 'none', cursor: index < (editData.photo_urls || []).length - 1 ? 'pointer' : 'default', opacity: index < (editData.photo_urls || []).length - 1 ? 1 : 0.3, padding: 2 }}><ChevronDown size={18} /></button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <Field label="Name" value={selectedVenueApp.map_name || selectedVenueApp.restaurant_name} />
            <Field label="Description" value={selectedVenueApp.map_description || selectedVenueApp.venue_type} />
            <Field label="Main Photo" value={selectedVenueApp.photo_urls?.length ? 'Auto (first photo)' : 'No photo uploaded'} />
          </div>
        )}
      </Card>

      <Card style={{ marginBottom: 12 }} data-testid="admin-venue-application-details-card">
        <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Application Details (7 Steps)</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <AdminDetailSection
            dataTestId="admin-venue-contact-details-section"
            title="Contact details"
            items={[
              { label: 'Name', value: `${selectedVenueApp.first_name} ${selectedVenueApp.last_name}`.trim() },
              { label: 'Phone', value: `${selectedVenueApp.phone_country_code || ''} ${selectedVenueApp.phone || ''}`.trim() },
              { label: 'Email', value: selectedVenueApp.contact_email },
            ]}
          />
          <AdminDetailSection
            dataTestId="admin-venue-business-details-section"
            title="Business details"
            items={[
              { label: 'Restaurant', value: selectedVenueApp.restaurant_name },
              { label: 'Legal name', value: selectedVenueApp.legal_business_name },
              { label: 'VAT', value: selectedVenueApp.business_vat },
            ]}
          />
          {(() => {
            const v = selectedVenueApp.aade_verification;
            const status = v?.status || (selectedVenueApp.business_vat ? 'not_attempted' : '');
            if (!status) return null;
            const tone = AADE_STATUS_TONE[status] || '#6B7280';
            const isVerified = status === 'verified';
            const runRecheck = async () => {
              setAadeRechecking(true);
              try {
                const token = localStorage.getItem('token');
                const res = await axios.post(
                  `${backendUrl}/api/admin/venue-applications/${selectedVenueApp.id}/aade-recheck`,
                  {},
                  { headers: token ? { Authorization: `Bearer ${token}` } : {} },
                );
                setSelectedVenueApp((prev) => (prev ? { ...prev, aade_verification: res.data } : prev));
                toast.success(`AADE re-check: ${AADE_STATUS_LABEL[res.data.status] || res.data.status}`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'AADE re-check failed');
              } finally {
                setAadeRechecking(false);
              }
            };
            return (
              <div
                data-testid="admin-venue-aade-verification-section"
                style={{
                  gridColumn: '1 / -1',
                  padding: 12,
                  borderRadius: 12,
                  background: `${tone}0F`,
                  border: `1px solid ${tone}55`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {isVerified ? <ShieldCheck size={16} color={tone} strokeWidth={2.2} /> : <ShieldAlert size={16} color={tone} strokeWidth={2.2} />}
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: tone, letterSpacing: '0.02em' }}>
                      AADE verification · {AADE_STATUS_LABEL[status] || status}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={runRecheck}
                    disabled={aadeRechecking || !selectedVenueApp.business_vat}
                    data-testid="admin-venue-aade-recheck-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', borderRadius: 999,
                      border: `1px solid ${tone}55`, background: '#FFFFFF',
                      color: tone, fontWeight: 700, fontSize: 12,
                      cursor: aadeRechecking ? 'wait' : 'pointer',
                    }}
                  >
                    <RefreshCw size={12} strokeWidth={2.2} className={aadeRechecking ? 'address-loader-spin' : ''} />
                    {aadeRechecking ? 'Re-checking…' : 'Re-check now'}
                  </button>
                </div>
                {v ? (
                  <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, fontSize: 12.5, color: '#3B404B' }}>
                    {v.legal_name_typed ? <div><span style={{ color: '#6B7280', marginRight: 6 }}>Typed:</span><strong>{v.legal_name_typed}</strong></div> : null}
                    {v.legal_name_returned ? <div><span style={{ color: '#6B7280', marginRight: 6 }}>AADE legal:</span><strong>{v.legal_name_returned}</strong></div> : null}
                    {v.trade_name_returned ? <div><span style={{ color: '#6B7280', marginRight: 6 }}>AADE trade:</span><strong>{v.trade_name_returned}</strong></div> : null}
                    {v.kad ? <div><span style={{ color: '#6B7280', marginRight: 6 }}>KAD:</span><strong>{v.kad}</strong>{v.kad_description ? ` — ${v.kad_description}` : ''}</div> : null}
                    {v.legal_status ? <div><span style={{ color: '#6B7280', marginRight: 6 }}>Legal form:</span><strong>{v.legal_status}</strong></div> : null}
                    {typeof v.match_ratio === 'number' && v.match_ratio > 0 ? <div><span style={{ color: '#6B7280', marginRight: 6 }}>Match:</span><strong>{Math.round(v.match_ratio * 100)}%</strong></div> : null}
                    {v.error_message ? <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6B7280', marginRight: 6 }}>Detail:</span>{v.error_message}</div> : null}
                    {v.looked_up_at ? <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#9CA3AF' }}>Last checked {formatEuropeanDateTime(v.looked_up_at)}</div> : null}
                  </div>
                ) : (
                  <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#6B7280' }}>
                    This venue was submitted before the AADE verification rollout. Tap Re-check now to fetch a fresh classification.
                  </p>
                )}
              </div>
            );
          })()}
          <AdminDetailSection
            dataTestId="admin-venue-address-section"
            title="Address"
            items={[
              { label: 'Country', value: selectedVenueApp.country },
              { label: 'Region', value: selectedVenueApp.region },
              { label: 'City', value: selectedVenueApp.city },
              { label: 'Street', value: selectedVenueApp.street },
              { label: 'Zip', value: selectedVenueApp.zip_code },
              { label: 'Coordinates', value: selectedVenueApp.coordinates },
            ]}
          />
          <AdminDetailSection
            dataTestId="admin-venue-social-media-section"
            title="Social media"
            items={[
              { label: 'Website', value: selectedVenueApp.website },
              { label: 'Instagram', value: selectedVenueApp.instagram },
              { label: 'Facebook', value: selectedVenueApp.facebook },
              { label: 'TikTok', value: selectedVenueApp.tiktok },
            ]}
          />
          <AdminDetailSection
            dataTestId="admin-venue-setup-section"
            title="Venue setup"
            items={[
              { label: 'Type', value: selectedVenueApp.venue_type },
              { label: 'Customer phone', value: selectedVenueApp.customer_phone },
              { label: 'Current step', value: selectedVenueApp.current_step ? `${selectedVenueApp.current_step}/7` : '' },
            ]}
          />
          <AdminDetailSection
            dataTestId="admin-venue-owner-account-section"
            title="Owner account"
            items={[
              { label: 'Name', value: selectedVenueApp.user?.name },
              { label: 'Email', value: selectedVenueApp.user?.email },
              { label: 'Role', value: selectedVenueApp.user?.role },
              { label: 'Verified', value: selectedVenueApp.user?.email_verified ? 'Yes' : 'No' },
            ]}
          />
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }} data-testid="admin-venue-operating-hours-card">
        <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Operating Hours</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          {selectedVenueApp.operating_hours && Object.entries(selectedVenueApp.operating_hours).length ? Object.entries(selectedVenueApp.operating_hours).map(([day, hours]) => {
            const hoursText = typeof hours === 'object' && hours !== null
              ? [hours.open, hours.close].filter(Boolean).join(' – ') || JSON.stringify(hours)
              : String(hours || '—');
            return (
              <div key={day} style={adminDetailFieldCardStyle}>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#8A839F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{day}</p>
                <p style={{ margin: '6px 0 0', fontSize: 14, fontWeight: 600, color: '#111' }}>{hoursText}</p>
              </div>
            );
          }) : <p style={{ fontSize: 13, color: '#999', margin: 0 }}>No operating hours added yet.</p>}
        </div>
      </Card>

      <AdminVenueMediaCard
        key={`media-${selectedVenueApp.id}`}
        backendUrl={backendUrl}
        selectedVenueApp={selectedVenueApp}
        setSelectedVenueApp={setSelectedVenueApp}
      />

      <Card style={{ marginTop: 12 }} data-testid="admin-venue-communication-card">
        <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>Communication</p>
        <p style={{ fontSize: 13, color: '#666', margin: '0 0 10px' }}>Start a separate admin conversation with this venue owner.</p>
        <button onClick={() => startAdminConversation(selectedVenueApp.user?.id, selectedVenueApp.user?.name || selectedVenueApp.contact_email)} data-testid={`admin-start-venue-chat-${selectedVenueApp.id}`} style={{ width: '100%', padding: '12px 14px', borderRadius: 14, border: '1px solid rgba(112, 63, 246, 0.1)', cursor: 'pointer', background: '#FFFFFF', boxShadow: 'var(--premium-shadow-nested)', fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700 }}>
          Start admin ↔ venue chat
        </button>
      </Card>
    </div>
  );
};